import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Button } from "../components/ui/button";
import { MENU } from "../lib/menuData";
import { IMAGES, RESTAURANT } from "../lib/restaurant";
import { Phone } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

function MenuItem({ name, desc, price, lang, weight }) {
  const description = desc?.[lang] || "";
  return (
    <div className="grid grid-cols-12 gap-4 items-baseline py-4 border-b border-charcoal/10 last:border-0" data-testid="menu-item">
      <div className="col-span-12 sm:col-span-9">
        <div className="flex items-baseline gap-3">
          <h4 className="font-serif text-xl text-charcoal">{name}</h4>
          <span className="hidden sm:block flex-1 border-b border-dotted border-charcoal/25 translate-y-[-4px]" />
        </div>
        {description && <p className="text-charcoal/65 text-sm mt-1 leading-relaxed">{description}</p>}
        {weight && <p className="text-charcoal/50 text-xs mt-1 italic">{weight}</p>}
      </div>
      <div className="col-span-12 sm:col-span-3 sm:text-right">
        <span className="font-serif text-xl text-wine whitespace-nowrap" data-testid="menu-item-price">
          {price === "—" ? "—" : `${price} PLN`}
        </span>
      </div>
    </div>
  );
}

export default function Menu() {
  const { t, lang } = useLang();
  return (
    <div data-testid="menu-page" className="pt-20 bg-cream">
      <section className="bg-charcoal text-cream py-24 sm:py-28 relative overflow-hidden">
        <div className="absolute right-0 top-0 w-1/2 h-full opacity-20 hidden lg:block">
          <img src={IMAGES.pasta} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <p className="uppercase-track text-xs text-gold mb-6">{t.menu.eyebrow}</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] mb-6">
            {t.menu.title1} <span className="italic text-gold">{t.menu.title2}</span>
          </h1>
          <p className="text-cream/85 text-lg max-w-2xl leading-relaxed">{t.menu.intro}</p>
        </div>
      </section>

      <div className="sticky top-20 z-30 bg-cream/95 backdrop-blur border-b border-charcoal/10">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 overflow-x-auto">
          <div className="flex gap-6 py-4 whitespace-nowrap">
            {MENU.map((cat) => (
              <a key={cat.id} href={`#${cat.id}`} data-testid={`menu-nav-${cat.id}`} className="text-xs uppercase-track text-charcoal/70 hover:text-wine transition">
                {cat.name[lang]}
              </a>
            ))}
          </div>
        </div>
      </div>

      <section className="py-16">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 lg:px-12">
          {/* Mobile accordion */}
          <div className="lg:hidden">
            <Accordion type="multiple" defaultValue={[MENU[0].id]} data-testid="menu-accordion">
              {MENU.map((cat) => (
                <AccordionItem key={cat.id} value={cat.id} id={cat.id} className="border-charcoal/15">
                  <AccordionTrigger data-testid={`menu-trigger-${cat.id}`} className="font-serif text-2xl text-charcoal hover:no-underline hover:text-wine py-6">
                    {cat.name[lang]}
                  </AccordionTrigger>
                  <AccordionContent>
                    {cat.description && (
                      <p className="text-charcoal/65 italic text-sm mb-3">{cat.description[lang]}</p>
                    )}
                    <div>
                      {cat.items.map((it, i) => <MenuItem key={i} {...it} lang={lang} />)}
                    </div>
                    {cat.footnote && <p className="text-charcoal/55 text-xs mt-4 italic">{cat.footnote[lang]}</p>}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* Desktop open */}
          <div className="hidden lg:block space-y-20">
            {MENU.map((cat) => (
              <section key={cat.id} id={cat.id} className="scroll-mt-44" data-testid={`menu-cat-${cat.id}`}>
                <div className="border-b-2 border-wine pb-4 mb-8 flex items-end justify-between gap-6">
                  <h2 className="font-serif text-4xl text-charcoal">{cat.name[lang]}</h2>
                  {cat.description && (
                    <p className="text-charcoal/55 italic text-sm max-w-md text-right hidden md:block">
                      {cat.description[lang]}
                    </p>
                  )}
                </div>
                <div>
                  {cat.items.map((it, i) => <MenuItem key={i} {...it} lang={lang} />)}
                </div>
                {cat.footnote && <p className="text-charcoal/55 text-xs mt-4 italic">{cat.footnote[lang]}</p>}
              </section>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-wine text-cream py-20">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 flex flex-col lg:flex-row items-center justify-between gap-8">
          <div>
            <h2 className="font-serif text-3xl sm:text-4xl mb-2">{t.menu.ctaTitle}</h2>
            <p className="text-cream/85">{t.menu.callLabel} <a href={`tel:${RESTAURANT.phoneRaw}`} className="underline">{RESTAURANT.phone}</a></p>
          </div>
          <div className="flex gap-3">
            <a href={`tel:${RESTAURANT.phoneRaw}`}>
              <Button data-testid="menu-call" className="rounded-none bg-cream text-charcoal hover:bg-gold uppercase-track text-xs h-12 px-7">
                <Phone className="h-4 w-4 mr-2" /> {t.menu.ctaCall}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
